import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from './FieldHeader';
import FieldDateInput from './FieldDateInput';
import Tooltip from '../../misc/Tooltip';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class DateField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    tz: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    wrapperStyles: PropTypes.object,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,
    titleTextOverflow: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    this.state = { focused: false };
  }

  render() {

    const {

      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,
      decorators,
      onDecoratorClick,
      fieldMode,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,
      format,
      tz,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,
      titleTextOverflow,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
    } = this.props;


    const getTooltip = () => {
      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );
    };

    return (
      <StyledWrapper {...{ margin }} style={wrapperStyles || {}}>
        <StyledInternalWrapper>
          <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
            <FieldTitle
              {...{
                title,
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleTextAlign,
                titleFontStyle,
                titleFontWeight,
                titleTextTransform,
                titleTextDecoration,
                titleTextOverflow,
              }}
            />
            <FieldDecorators
              decorators={decorators}
              fieldMode={fieldMode}
              fieldId={fieldId}
              fieldIdentifier={fieldIdentifier}
              fieldPositionId={fieldPositionId}
              onDecoratorClick={onDecoratorClick}
            />
          </StyledFieldHeader>
          {getTooltip()}
          <FieldDateInput
            {...{
              primaryColor,
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
            }}
            disabled={disabled}
            value={value}
            timezone={tz}
            enableTimezoneAnchor={true}
            format={format}
            onChange={this.handleChange}
            onClick={this.handleFocus}
            onClose={this.handleClose}
          />
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }
  
  handleChange = (changes) => {

    const { focused } = this.state;
    const { fieldId, fieldIdentifier, fieldPositionId, tz, type, disabled, value, onChange, onBlur } = this.props;

    if (focused) this.setState({ focused: false });

    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const args = [fieldId, fieldIdentifier, fieldPositionId, changes];
    // Null values are for signer, collectionFieldParams, eventType inside gridlayout handleFieldUpdate
    if (!tz) args.push(null, null, null, localTimezone)

    if (!disabled && value !== changes) {
      onChange(...args);
    }

  }

  handleFocus = (e) => {
    const { fieldId, fieldIdentifier, fieldPositionId, onFocus, disabled } = this.props;
    const { focused } = this.state;

    if (!focused) this.setState({ focused: true });

    if (!disabled && onFocus && !focused) {
      onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: e.blur });
    }
  }

  handleClose = () => {
    const { fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
  }

}

export default DateField;